import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const data = await api.get('/invoices');
      setInvoices(data.invoices || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: { background: '#f3f4f6', color: '#374151' },
      sent: { background: '#dbeafe', color: '#1d4ed8' },
      paid: { background: '#dcfce7', color: '#166534' },
      overdue: { background: '#fef2f2', color: '#dc2626' }
    };
    const style = styles[status] || styles.draft;
    return (
      <span style={{
        padding: '0.25rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        ...style
      }}>
        {status}
      </span>
    );
  };

  const filteredInvoices = filter === 'all' 
    ? invoices 
    : invoices.filter(inv => inv.status === filter);

  const totalOutstanding = invoices
    .filter(inv => inv.status !== 'paid')
    .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);

  const totalPaid = invoices
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + parseFloat(inv.total || 0), 0);

  const downloadCSV = () => {
    const headers = ['Invoice #', 'Patient', 'Date', 'Amount', 'Status'];
    const rows = filteredInvoices.map(inv => [
      inv.invoiceNumber,
      inv.patient ? `${inv.patient.firstName} ${inv.patient.lastName}` : '',
      formatDate(inv.invoiceDate),
      parseFloat(inv.total || 0).toFixed(2),
      inv.status
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `invoices_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV downloaded');
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="loading-spinner"></div>
        <p>Loading invoices...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1>Invoices</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div className="card" style={{ background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', color: 'white' }}>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Total Invoices</div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>{invoices.length}</div>
        </div>
        <div className="card" style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white' }}>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Paid</div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>£{totalPaid.toFixed(2)}</div>
        </div>
        <div className="card" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white' }}>
          <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>Outstanding</div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>£{totalOutstanding.toFixed(2)}</div>
        </div>
      </div>

      <div className="card">
        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {['all', 'draft', 'sent', 'paid', 'overdue'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '6px',
                  background: filter === status ? '#3b82f6' : '#f3f4f6',
                  color: filter === status ? 'white' : '#374151',
                  cursor: 'pointer',
                  fontWeight: 500,
                  textTransform: 'capitalize'
                }}
              >
                {status}
              </button>
            ))}
          </div>
          <button
            onClick={downloadCSV}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              background: 'white',
              color: '#374151',
              cursor: 'pointer',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <span>Download CSV</span>
          </button>
        </div>

        {filteredInvoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <p>No invoices found</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
              Create invoices from patient profiles
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Patient</th>
                <th>Date</th>
                <th>Amount</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map(invoice => (
                <tr key={invoice.id}>
                  <td>
                    <Link to={`/invoices/${invoice.id}`} style={{ color: '#ec4899', fontWeight: 600 }}>
                      {invoice.invoiceNumber}
                    </Link>
                  </td>
                  <td>
                    {invoice.patient ? (
                      <Link to={`/patients/${invoice.patient.id}`} style={{ color: '#3b82f6' }}>
                        {invoice.patient.firstName} {invoice.patient.lastName}
                      </Link>
                    ) : '-'}
                  </td>
                  <td>{formatDate(invoice.invoiceDate)}</td>
                  <td style={{ fontWeight: 600 }}>£{parseFloat(invoice.total || 0).toFixed(2)}</td>
                  <td>{getStatusBadge(invoice.status)}</td>
                  <td>
                    <Link to={`/invoices/${invoice.id}`} className="btn btn-sm" style={{ fontSize: '0.75rem' }}>
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Invoices;
